import React, { useState, useEffect } from 'react';
import SearchSelect from '../token/SearchSelect';
import { Entity } from '../../types/cms';
import { useInlineEdit } from '../../hooks/useInlineEdit';

interface RelationshipCellProps {
  videoId: string;
  field: 'agencyId' | 'enterpriseId';
  currentEntity: Entity | null;
  allEntities: Entity[];
  onUpdate: (videoId: string, field: string, newEntity: Entity) => void;
  onRollback: (videoId: string) => void;
}

export default function RelationshipCell({
  videoId,
  field,
  currentEntity,
  allEntities,
  onUpdate,
  onRollback,
}: RelationshipCellProps) {
  const [displayEntity, setDisplayEntity] = useState<Entity | null>(currentEntity);
  const [originalEntity, setOriginalEntity] = useState<Entity | null>(currentEntity);

  // Sync with props when they change
  useEffect(() => {
    setDisplayEntity(currentEntity);
    setOriginalEntity(currentEntity);
  }, [currentEntity]);

  const { isEditing, isSaving, setIsEditing, handleSave } = useInlineEdit(
    'videos',
    videoId,
    field,
    (newEntityId: string) => {
      // Optimistic update
      const newEntity = allEntities.find(e => e.id === newEntityId) || null;
      if (newEntity) {
        setDisplayEntity(newEntity);
        onUpdate(videoId, field, newEntity);
      }
    },
    () => {
      // Rollback on error
      setDisplayEntity(originalEntity);
      onRollback(videoId);
    }
  );

  const handleSelect = async (entity: Entity | null) => {
    if (entity) {
      await handleSave(entity.id);
    }
  };

  // Display mode
  if (!isEditing) {
    return (
      <div
        onClick={() => !isSaving && setIsEditing(true)}
        className={`
          flex items-center gap-2 px-2 py-1 rounded cursor-pointer
          transition-colors group
          ${displayEntity
            ? 'hover:bg-neutral-100 border border-transparent hover:border-neutral-300'
            : 'border border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
          ${isSaving ? 'opacity-50 cursor-wait' : ''}
        `}
      >
        {displayEntity ? (
          <>
            {displayEntity.logoURL && displayEntity.logoURL.length > 0 && (
              <img
                src={displayEntity.logoURL[0]}
                alt={displayEntity.name}
                className="w-4 h-4 rounded object-cover"
              />
            )}
            <span className="text-sm text-gray-700 group-hover:text-gray-900">
              {displayEntity.name}
            </span>
            <svg
              className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </>
        ) : (
          <span className="text-sm text-gray-400 group-hover:text-gray-600">
            Not assigned
          </span>
        )}
      </div>
    );
  }

  // Edit mode - show SearchSelect inline
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <SearchSelect
        options={allEntities}
        onSelect={handleSelect}
        selectedValue={displayEntity}
        placeholder="Select entity..."
        accessor="name"
        loading={isSaving}
      />
    </div>
  );
}
